import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

// POC: vbegin comes from the src query string, or the vbegin prop as fallback.
const ParliamentLiveVideoPlayer = forwardRef(({ src, startTimestamp, vbegin, autoPlay }, ref) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const vbeginRef = useRef(null);

  useEffect(() => {
    if (!src || !containerRef.current) return;

    vbeginRef.current = Number(new URL(src).searchParams.get('vbegin')) || vbegin || 0;

    if (!playerRef.current) {
      // video.js owns this node, so it's created imperatively, not via React's JSX.
      const videoElement = document.createElement('video-js');
      videoElement.classList.add('vjs-big-play-centered');
      containerRef.current.appendChild(videoElement);

      playerRef.current = videojs(videoElement, {
        controls: true,
        fluid: true,
        preload: 'auto',
        sources: [{ src, type: 'application/x-mpegURL' }],
      });
    } else {
      playerRef.current.src({ src, type: 'application/x-mpegURL' });
    }
  }, [src]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || !startTimestamp) return;

    const seconds = Math.max(0, startTimestamp - vbeginRef.current);
    const seekAndMaybePlay = () => {
      player.currentTime(seconds);
      if (autoPlay) player.play()?.catch(() => {});
    };

    if (player.readyState() > 0) {
      seekAndMaybePlay();
    } else {
      player.one('loadedmetadata', seekAndMaybePlay);
    }
  }, [autoPlay]);

  useEffect(() => {
    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  useImperativeHandle(ref, () => ({
    seekToTimestamp: (unixTimestamp) => {
      const player = playerRef.current;
      if (!player || !unixTimestamp) return;
      const seconds = Math.max(0, unixTimestamp - vbeginRef.current);
      player.currentTime(seconds);
      player.play();
    },
    getDuration: () => playerRef.current?.duration() ?? 0,
  }));

  return (
    <div data-vjs-player className='parliament-live-video-player'>
      <div ref={containerRef} />
    </div>
  );
});

export default ParliamentLiveVideoPlayer;
